import { Input, ModalNooco } from '@photocarbone/nooco-ui';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostProjectFromSimulator } from '../../../../hooks/useQuery/projectSimulator';
import { TextField } from '@material-ui/core';
import { StateDTO } from '../../../models/GeneralDTO';
import { useSelector } from 'react-redux';
import useCase from '../../../projects/HomeProject/useCase/useCase';
import { useEffect } from 'react';

function SummaryModal({
  openModal,
  setOpenModal,
}: {
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  const { mutate, isLoading } = usePostProjectFromSimulator();
  const { companies, isSuperAdmin } = useSelector(
    (state: StateDTO) => state.projects
  );
  const { selectedTypeProjectId } = useSelector(
    (state: StateDTO) => state.projects.creationProject
  );
  const { arianeCompany } = useSelector(
    (state: StateDTO) => state.projects.projectsListMonitoring
  );
  const { selectedCompanyId } = arianeCompany;
  const { selectHomeBreadCrumb } = useCase();

  const filteredCompanies = isSuperAdmin
    ? companies
    : companies.filter((company) =>
        company.ProjectTypes.includes(selectedTypeProjectId)
      );

  const isCompanySelectedInNavigation = typeof selectedCompanyId === 'number';
  const getCompanyFromId = (companyId) =>
    filteredCompanies.find((company) => company.id === companyId) || null;

  const {
    setValue,
    control,
    formState: { errors },
    trigger,
    getValues,
  } = useForm();

  const { projectName, company } = getValues();


  return (
    <ModalNooco
      title={t('APP.PROJECT.SIMULATOR.SUMMARY.MODAL.TITLE', {
        defaultValue: 'Créer un projet',
      })}
      loading={isLoading}
      isOpen={openModal}
      cancelFunction={() => setOpenModal(false)}
      validateFunction={async () => {
        const isValid = await trigger();
        if (isValid) {
          mutate({
            name: projectName,
            companyId: company?.id,
          });
        }
      }}
      closeOnOutsideClick={true}
      disabled={false}
      closeIcon={false}
    >
      <label htmlFor="projectName">Nom du projet</label>
      <Controller
        name="projectName"
        control={control}
        rules={{
          validate: (value) => {
            let forbiddenCharacters = [
              '/',
              '{',
              '^',
              '}',
              '%',
              '`',
              ']',
              '“',
              '>',
              '[',
              '~',
              '<',
              '#',
              '|',
              `\\`,
            ];

            let errors = forbiddenCharacters.filter((char) => value.includes(char));

            if (errors.length === 1) {
              return t(
                'APP.GENERALFORM.INDEX.PH_013EB1',

                {
                  char: forbiddenCharacters.find((char) => value.includes(char)),
                  defaultValue:
                    'Caractère {char} interdit. Veuillez svp le supprimer ou le remplacer',
                }
              );
            } else if (errors.length > 1) {
              return t(
                'APP.GENERALFORM.INDEX.PH_EB7F22',

                {
                  chars: errors.map((el) => `${el} `),
                  defaultValue:
                    'Les caractères suivants sont interdits : {chars}. Veuillez svp les supprimer ou les remplacer',
                }
              );
            }

            return true;
          },
          required: t('APP.GENERALFORM.INDEX.PH_6CED8D', {
            defaultValue: 'Veuillez renseigner un nom.',
          }),
        }}
        render={({ field }) => (
          <Input
            {...field}
            id="projectName"
            style={{ width: '100%' }}
            disabledDebouncing={true}
            onBlur={() => trigger('projectName')}
            error={Boolean(errors.projectName)}
            placeholder="Nom de projet"
          />
        )}
      />
      <p className="error-message-form">{errors?.projectName?.message}</p>

      <label htmlFor="company">Entreprises</label>
      {filteredCompanies.length === 1 ? (
        <Controller
          name="company"
          control={control}
          defaultValue={filteredCompanies[0]}
          rules={{
            required: t('APP.GENERALFORM.INDEX.PH_DD5F1C', {
              defaultValue: 'Veuillez renseigner une entreprise.',
            }),
          }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              id="checkboxes-tags-demo"
              options={filteredCompanies.sort(
                (a, b) => a.name.localeCompare(b.name) - b.name.localeCompare(a.name)
              )}
              value={filteredCompanies[0]}
              disabled
              noOptionsText={t('APP.GENERALFORM.INDEX.PH_48B4CE', {
                defaultValue: 'Aucun résultat trouvé',
              })}
              size={'small'}
              getOptionLabel={(option) => option.name}
              renderOption={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('APP.GENERALFORM.INDEX.PH_1817A2', {
                    defaultValue: 'Entreprises',
                  })}
                  variant="outlined"
                  className={`researchBar ${
                    errors?.company ? 'error-border-form' : ''
                  }`}
                />
              )}
            />
          )}
        />
      ) : filteredCompanies.length > 1 ? (
        <Controller
          name="company"
          control={control}
          rules={{
            required: t('APP.GENERALFORM.INDEX.PH_95F780', {
              defaultValue: 'Veuillez renseigner une entreprise.',
            }),
          }}
          defaultValue={
            isCompanySelectedInNavigation
              ? getCompanyFromId(selectedCompanyId)
              : null
          }
          render={({ field }) => (
            <Autocomplete
              {...field}
              disabled={false}
              onChange={(data, newValue) => {
                selectHomeBreadCrumb();
                setValue('company', newValue ? newValue : undefined);
              }}
              id="checkboxes-tags-demo"
              options={filteredCompanies.sort(
                (a, b) => a.name.localeCompare(b.name) - b.name.localeCompare(a.name)
              )}
              noOptionsText={t('APP.GENERALFORM.INDEX.PH_904D59', {
                defaultValue: 'Aucun résultat trouvé',
              })}
              size={'small'}
              getOptionLabel={(option) => option.name}
              renderOption={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  onBlur={() => trigger('company')}
                  error={Boolean(errors.company)}
                  placeholder={t('APP.GENERALFORM.INDEX.PH_4BB861', {
                    defaultValue: 'Entreprises',
                  })}
                  variant="outlined"
                  className={`researchBar ${
                    errors?.company ? 'error-border-form' : ''
                  }`}
                />
              )}
            />
          )}
        />
      ) : null}
      <p className="error-message-form">{errors?.company?.message}</p>
    </ModalNooco>
  );
}

export default SummaryModal;
